// --------------------------------------------------------------
// Created On: 2021-09-29
// Author: Zachary Thomas
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect, useMemo } from "react";
import { API, MIN_FULL_NAME_LENGTH, MAX_FULL_NAME_LENGTH } from "../../constants/miscellaneous";
import Spinner from "../../components/Spinner/Spinner";
import Card from "../../components/Card/Card";
import Error from "../../components/Error/Error";
import Success from "../../components/Success/Success";
import Error500Page from "../Error500Page/Error500Page";
import useApi from "../../hooks/useApi";
import getApiError from "../../utilities/api/getApiError";
import apiRequest from "../../utilities/api/apiRequest";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import useReduxSelector from "../../hooks/useReduxSelector";
import PhoneNumberInput from "../../components/PhoneNumberInput/PhoneNumberInput";
import userHasCrossCompanyAccess from "../../utilities/userHasCrossCompanyAccess";
import PermanentlyDeleteModal from "../../components/PermanentlyDeleteModal/PermanentlyDeleteModal";
import PasswordConfirmModal from "src/components/PasswordConfirmModal/PasswordConfirmModal";
import MfaConfirmModal from "src/components/MfaConfirmModal/MfaConfirmModal";
import { setCurrentUser } from "src/redux/features/currentUserSlice";
import { setSessionToken } from "src/redux/features/sessionTokenSlice";
import { setIdToken } from "src/redux/features/idTokenSlice";
import { useNavigate } from "react-router-dom";
import { PostAuthenticateResponseBody } from "src/types/api/PostAuthenticateResponseBody";
import styles from "./ChangeProfilePage.module.scss";

// Page for changing user profile settings.
export default function ChangeProfilePage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [showPermanentlyDeleteSelfModal, setShowPermanentlyDeleteSelfModal] = useState<boolean>(false);
  const [showDisableMfaPasswordModal, setShowDisableMfaPasswordModal] = useState<boolean>(false);
  const [showDisableMfaCodeModal, setShowDisableMfaCodeModal] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [mfaErrorMessage, setMfaErrorMessage] = useState<string>("");
  const [deleteUserErrorMessage, setDeleteUserErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [newName, setNewName] = useState<string>("");
  const [previousCallingCode, setPreviousCallingCode] = useState<string>("1");
  const [previousPhoneNumber, setPreviousPhoneNumber] = useState<string>("");
  const [callingCode, setCallingCode] = useState<string>("1");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [userId, setUserId] = useState<number>(0);
  const [confirmationPassword, setConfirmationPassword] = useState<string>("");
  const [confirmationCode, setConfirmationCode] = useState<string>("");
  const currentUser = useReduxSelector((state) => state.currentUser);
  const sessionToken = useReduxSelector((state) => state.sessionToken);
  const navigate = useNavigate();
  const dispatch = useReduxDispatch();
  const changesMadeToInputs = useMemo(
    () =>
      getChangesMadeToInputs(
        callingCode,
        previousCallingCode,
        phoneNumber,
        previousPhoneNumber,
        newName,
        currentUser.userName
      ),
    [callingCode, previousCallingCode, phoneNumber, previousPhoneNumber, newName, JSON.stringify(currentUser)]
  );

  // Get account info.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      method: "GET",
      url: `${API}/company/${
        userHasCrossCompanyAccess() ? currentUser.originalCompanyId : currentUser.companyId
      }/user/${currentUser.userId}`,
    },
    async (response: Response, responseBody: GetResponseBody) => {
      if (response.ok && responseBody) {
        dispatch(
          setCurrentUser({
            userId: currentUser.userId,
            userName: responseBody.name,
            emailAddress: responseBody.emailAddress,
            originalCompanyId: currentUser.originalCompanyId,
            companyId: currentUser.companyId,
            companyName: currentUser.companyName,
            isCompanyHidden: currentUser.isCompanyHidden,
            permissions: currentUser.permissions,
            isPackager: currentUser.isPackager,
            mfaEnabled: currentUser.mfaEnabled,
          })
        );
        setNewName(responseBody.name);
        setUserId(responseBody.userId);
        if (
          responseBody.phoneNumberCountryCode !== null &&
          responseBody.phoneNumberCountryCode !== undefined &&
          responseBody.phoneNumber !== null &&
          responseBody.phoneNumber !== undefined
        ) {
          setCallingCode(responseBody.phoneNumberCountryCode);
          setPreviousCallingCode(responseBody.phoneNumberCountryCode);
          setPreviousPhoneNumber(responseBody.phoneNumber);
          setPhoneNumber(responseBody.phoneNumber);
        } else {
          setCallingCode("1");
          setPhoneNumber("");
        }
        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
      }
      setLoading(false);
    },
    []
  );

  // If an input is being edited, remove any old messages.
  useEffect(() => {
    setErrorMessage("");
    setSuccessMessage("");
  }, [newName, phoneNumber, callingCode]);

  // Checks if any of the user input values have been modified.
  function getChangesMadeToInputs(
    callingCode: string,
    previousCallingCode: string,
    phoneNumber: string,
    previousPhoneNumber: string,
    name: string,
    previousName: string
  ): boolean {
    return callingCode !== previousCallingCode || phoneNumber !== previousPhoneNumber || name !== previousName;
  }

  // Validate the account settings.
  function accountIsValid(): boolean {
    if (newName.trim().length < MIN_FULL_NAME_LENGTH || newName.trim().length > MAX_FULL_NAME_LENGTH) {
      setSuccessMessage("");
      setErrorMessage(`Your name must be between ${MIN_FULL_NAME_LENGTH} and ${MAX_FULL_NAME_LENGTH} characters long.`);
      return false;
    } else {
      return true;
    }
  }

  // Save the account settings.
  async function saveChanges(): Promise<void> {
    let requestPhoneNumber: string | null = null;
    let requestCallingCode: string | null = null;
    if (phoneNumber !== undefined && callingCode !== undefined && phoneNumber.length > 0) {
      // Remove the calling code in the phone number before sending it in the request body.
      const regexRemoveCodeFromStart = new RegExp(`^${callingCode}`);
      requestPhoneNumber = phoneNumber.replace(regexRemoveCodeFromStart, "");
      requestCallingCode = callingCode;
    }

    if (accountIsValid()) {
      const requestBody: RequestBody = {
        name: newName.trim(),
        phoneNumberCountryCode: requestCallingCode,
        phoneNumber: requestPhoneNumber,
      };

      setLoading(true);
      const [response] = (await apiRequest(
        `${API}/company/${currentUser.companyId}/user/${userId}/profile`,
        "PUT",
        requestBody
      )) as [Response, PutResponseBody];
      setLoading(false);

      if (response.ok) {
        dispatch(
          setCurrentUser({
            userId: currentUser.userId,
            userName: newName,
            emailAddress: currentUser.emailAddress,
            originalCompanyId: currentUser.originalCompanyId,
            companyId: currentUser.companyId,
            companyName: currentUser.companyName,
            isCompanyHidden: currentUser.isCompanyHidden,
            permissions: currentUser.permissions,
            isPackager: currentUser.isPackager,
            mfaEnabled: currentUser.mfaEnabled,
          })
        );
        setErrorMessage("");
        setSuccessMessage("Account settings have been saved.");
      } else {
        setSuccessMessage("");
        setErrorMessage(await getApiError(response, "Unable to update account."));
      }
    }
  }

  // Undo any changes that have been made since the page loaded.
  function undoChanges(): void {
    setNewName(currentUser.userName);
    setPhoneNumber(previousPhoneNumber);
    setCallingCode(previousCallingCode);
  }

  // Delete the current user's account.
  async function deleteSelf(): Promise<void> {
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/user/${currentUser.userId}`,
      "DELETE",
      null
    )) as [Response, DeleteResponseBody];
    setLoading(false);

    if (response.ok) {
      setDeleteUserErrorMessage("");
      dispatch(setIdToken(null));
      navigate("/login");
    } else {
      setShowPermanentlyDeleteSelfModal(false);
      setDeleteUserErrorMessage(await getApiError(response, "Unable to delete account."));
    }
  }

  // Attempts to login to get the access token that will be needed for disabling MFA.
  async function handleDisableMfaPassword(emailAddress: string, password: string): Promise<void> {
    const requestBody = {
      username: emailAddress,
      password: password,
    };

    setLoading(true);
    const [response, responseBody] = (await apiRequest(`${API}/registration/authenticate`, "POST", requestBody)) as [
      Response,
      PostAuthenticateResponseBody
    ];
    setLoading(false);

    if (
      response.ok &&
      responseBody &&
      responseBody.mfaChallengeRequired &&
      responseBody.sessionToken !== null &&
      responseBody.sessionToken !== ""
    ) {
      setMfaErrorMessage("");
      setShowDisableMfaPasswordModal(false);
      setShowDisableMfaCodeModal(true);
      dispatch(setSessionToken(responseBody.sessionToken));
    } else {
      setMfaErrorMessage(await getApiError(response, "Unable to disable MFA."));
    }
  }

  // Attempts to login to get the access token that will be needed for disabling MFA.
  async function handleDisableMfaCode(
    emailAddress: string,
    sessionToken: string | null,
    mfaCode: string
  ): Promise<void> {
    if (mfaCode.length === 0) {
      setMfaErrorMessage("Please enter authentication code.");
    } else {
      let accessToken: string | null = null;

      // Make sure we have a valid session token.
      if (sessionToken === null) {
        setMfaErrorMessage("Internal server error. Unable to complete token based MFA authentication check.");
        return;
      }

      // Start by making sure that the MFA code is correct.
      const mfaCodeRequestBody = {
        username: emailAddress,
        sessionToken: sessionToken,
        mfaCode: mfaCode,
      };
      setLoading(true);
      const [authResponse, authResponseBody] = (await apiRequest(
        `${API}/registration/authenticate`,
        "POST",
        mfaCodeRequestBody
      )) as [Response, PostAuthenticateResponseBody];
      if (
        authResponse.ok &&
        authResponseBody &&
        authResponseBody.accessToken !== null &&
        authResponseBody.accessToken !== ""
      ) {
        setMfaErrorMessage("");
        accessToken = authResponseBody.accessToken;
      } else {
        setLoading(false);
        setMfaErrorMessage(await getApiError(authResponse, "Unable to disable MFA."));
        return;
      }

      // If the MFA code was correct, then we can continue to disabling MFA for the user.
      const disableMfaRequestBody = {
        accessToken: accessToken,
      };
      setLoading(true);
      const [disableResponse, disableResponseBody] = (await apiRequest(
        `${API}/mfa/sms-disable`,
        "POST",
        disableMfaRequestBody
      )) as [Response, PostResponseBody];
      setLoading(false);
      if (disableResponse.ok && disableResponseBody) {
        setMfaErrorMessage("");
        dispatch(
          setCurrentUser({
            userId: currentUser.userId,
            userName: currentUser.userName,
            emailAddress: currentUser.emailAddress,
            originalCompanyId: currentUser.originalCompanyId,
            companyId: currentUser.companyId,
            companyName: currentUser.companyName,
            isCompanyHidden: currentUser.isCompanyHidden,
            permissions: currentUser.permissions,
            isPackager: currentUser.isPackager,
            mfaEnabled: false,
          })
        );
        setShowDisableMfaCodeModal(false);
        setConfirmationCode("");
        setConfirmationPassword("");
      } else {
        setMfaErrorMessage(await getApiError(disableResponse, "Unable to disable MFA."));
      }
    }
  }

  // Cancel disabling MFA.
  function handleCancelDisableMfa(): void {
    setShowDisableMfaPasswordModal(false);
    setShowDisableMfaCodeModal(false);
    setConfirmationPassword("");
    setConfirmationCode("");
    setMfaErrorMessage("");
  }

  return failedToLoad ? (
    <Error500Page />
  ) : (
    <div className={`${styles.body} p-4 col-12 col-xl-10 col-xxl-8 offset-xl-1 offset-xxl-2`}>
      <Spinner loading={loading} />

      <Card title="Account Information">
        <div className="mx-4 my-3">
          {/* Account info. */}
          <div className="row mb-3">
            <div className="col-12 col-lg-6 text-center">
              <p className={styles.profileInfo}>
                <i className="fa fa-fw fa-envelope-o" /> {currentUser.emailAddress}
              </p>
            </div>

            {currentUser.companyId === currentUser.originalCompanyId && (
              <div className="col-12 col-lg-6 text-center">
                <p className={styles.profileInfo}>
                  <i className="fa fa-fw fa-building-o" /> {currentUser.companyName}
                </p>
              </div>
            )}
          </div>

          {/* Name input. */}
          <div className="form-update-account mb-4">
            <label className="mb-3 font-weight-bold">Name</label>
            <input
              className="form-control mx-auto"
              type="text"
              maxLength={MAX_FULL_NAME_LENGTH}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>

          {/* Phone number input. */}
          <div className="form-update-account mb-5">
            <PhoneNumberInput
              labelText="Phone Number"
              labelClassName="mb-3 font-weight-bold"
              phoneNumber={phoneNumber}
              callingCode={callingCode}
              onChangePhoneNumber={(phoneNumber) => setPhoneNumber(phoneNumber)}
              onChangeCallingCode={(callingCode) => setCallingCode(callingCode)}
            />
          </div>

          {(errorMessage.length > 0 || successMessage.length > 0) && (
            <div className="status-messages mt-3">
              <Error message={errorMessage} />
              <Success message={successMessage} />
            </div>
          )}

          <div className="row">
            <div className="col-12 col-lg-3">
              <button
                data-test="delete-self-button"
                className={`${styles.profileButton} btn btn-danger my-3`}
                type="button"
                disabled={loading}
                onClick={() => setShowPermanentlyDeleteSelfModal(true)}
              >
                Delete Account
              </button>
            </div>

            <div className="col-12 col-lg-3">
              <button
                data-test="disable-mfa-button"
                className={`${styles.profileButton} btn btn-danger my-3`}
                type="button"
                disabled={loading || !currentUser.mfaEnabled}
                onClick={() => setShowDisableMfaPasswordModal(true)}
              >
                Disable MFA
              </button>
            </div>

            <div className="col-12 col-lg-3">
              <button
                data-test="save-changes-button"
                className={`${styles.profileButton} btn btn-primary my-3`}
                type="button"
                disabled={loading || !changesMadeToInputs}
                onClick={() => saveChanges()}
              >
                Save Changes
              </button>
            </div>
            <div className="col-12 col-lg-3">
              <button
                data-test="undo-changes-button"
                className={`${styles.profileButton} btn btn-secondary my-3`}
                type="button"
                disabled={loading || !changesMadeToInputs}
                onClick={() => undoChanges()}
              >
                Undo Changes
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Delete user account. */}
      <PermanentlyDeleteModal
        showModal={showPermanentlyDeleteSelfModal}
        title="Delete account?"
        content="Are you sure that you want to delete your current user account? If you delete yourself you will not be able to login to this account again."
        errorMessage={deleteUserErrorMessage}
        onDelete={() => deleteSelf()}
        onCancel={() => {
          setShowPermanentlyDeleteSelfModal(false);
          setDeleteUserErrorMessage("");
        }}
      />

      {/* Disable MFA - Password check. */}
      <PasswordConfirmModal
        showModal={showDisableMfaPasswordModal}
        title="Disable MFA?"
        content={
          "Are you sure you want to disable multi-factor authentication (MFA) on your account? " +
          "If MFA is required for your account, you will be logged out and have to setup an MFA device before " +
          "having application access again."
        }
        password={confirmationPassword}
        confirmText="Continue"
        errorMessage={mfaErrorMessage}
        onChangePassword={(confirmationPassword) => setConfirmationPassword(confirmationPassword)}
        onConfirm={() => handleDisableMfaPassword(currentUser.emailAddress, confirmationPassword)}
        onCancel={() => handleCancelDisableMfa()}
      />

      {/* Disable MFA - Code check. */}
      <MfaConfirmModal
        showModal={showDisableMfaCodeModal}
        title="Disable MFA?"
        content={
          "You should receive an MFA code shortly. Please enter the MFA code below to complete " +
          "the process of disabling MFA for this account."
        }
        code={confirmationCode}
        confirmText="Disable MFA"
        errorMessage={mfaErrorMessage}
        onChangeCode={(confirmationCode) => setConfirmationCode(confirmationCode)}
        onConfirm={() => handleDisableMfaCode(currentUser.emailAddress, sessionToken, confirmationCode)}
        onCancel={() => handleCancelDisableMfa()}
      />
    </div>
  );
}

interface GetResponseBody {
  userId: number;
  name: string;
  emailAddress: string;
  roleId: number;
  companyId: number;
  companyName: string;
  isCompanyHidden: boolean;
  permissions: string[];
  phoneNumberCountryCode: string | null;
  phoneNumber: string | null;
}

interface PostResponseBody {
  message: string;
}

interface PutResponseBody {
  message: string;
}

interface DeleteResponseBody {
  message: string;
}

interface RequestBody {
  name: string;
  phoneNumberCountryCode: string | null;
  phoneNumber: string | null;
}
